<template>
  <div class="identity-verification-view">
    <a-spin :spinning="loading" :tip="labels.view.loading">
      <a-row
        type="flex"
        justify="space-between"
        :gutter="16"
        :style="{ marginBottom: '15px' }"
      >
        <!-- Title -->
        <a-col :xs="24" :sm="24" :md="12">
          <h2 class="form-title">
            <a-icon :type="'form'" class="form-icon" />
          </h2>
        </a-col>
        <!-- Actions -->
        <a-col :xs="24" :sm="24" :md="12">
          <a-row
            type="flex"
            justify="end"
            :gutter="16"
            :style="{
              alignItems: 'flex-end',
              marginBottom: '0px',
            }"
          >
            <a-col>
              <a-button-group>
                <a-button
                  :type="'primary'"
                  ghost
                  @click="handleCancel"
                  icon="arrow-left"
                >
                  <span>{{ labels.view.goBack }}</span>
                </a-button>
                <a-button
                  v-if="isAuthorizedToReview"
                  type="danger"
                  @click="confirmReject"
                >
                  <span>{{ labels.view.reject }}</span>
                </a-button>
                <a-button
                  v-if="isAuthorizedToReview"
                  type="primary"
                  :loading="submitting"
                  ghost
                  @click="confirmApprove"
                >
                  <span>{{ labels.view.approve }}</span>
                </a-button>
              </a-button-group>
            </a-col>
          </a-row>
        </a-col>
      </a-row>

      <!-- SHOW IMAGES ALL NEXT TO EACH OTHERS AS CARDS -->
      <a-row
        v-if="identityVerification"
        :gutter="[16, 16]"
        type="flex"
        justify="center"
        :class="{ 'flex-reserve': isMobile }"
      >
        <a-col :xs="24" :sm="24" :md="16">
          <a-row v-if="identityVerification" :gutter="[16, 16]">
            <a-col :span="24">
              <h3>{{ labels.view.documents }}</h3>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12">
              <a-card hoverable style="width: 100%">
                <img
                  slot="cover"
                  :alt="labels.view.documentFront"
                  :src="identityVerification.document_front_path"
                  @click="
                    handlePreviewFile(identityVerification.document_front_path)
                  "
                />
                <a-card-meta :title="labels.view.documentFrontDescription">
                  <template slot="description">
                    {{ labels.view.documentFrontDescription }}
                  </template>
                </a-card-meta>
              </a-card>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12">
              <a-card hoverable style="width: 100%">
                <img
                  slot="cover"
                  :alt="labels.view.documentBack"
                  :src="identityVerification.document_back_path"
                  @click="
                    handlePreviewFile(identityVerification.document_front_path)
                  "
                />
                <a-card-meta :title="labels.view.documentBackDescription">
                  <template slot="description">
                    {{ labels.view.documentBackDescription }}
                  </template>
                </a-card-meta>
              </a-card>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12">
              <a-card hoverable style="width: 100%">
                <img
                  slot="cover"
                  :alt="labels.view.selfie"
                  :src="identityVerification.selfie_path"
                  @click="
                    handlePreviewFile(identityVerification.document_front_path)
                  "
                />
                <a-card-meta :title="labels.view.selfieDescription">
                  <template slot="description">
                    {{ labels.view.selfieDescription }}
                  </template>
                </a-card-meta>
              </a-card>
            </a-col>
          </a-row>
        </a-col>
        <a-col :xs="24" :sm="24" :md="8">
          <!-- SHOW STATUS AND DATES -->
          <a-row
            v-if="identityVerification"
            :gutter="16"
            :style="{ marginBottom: '15px' }"
            type="flex"
            justify="end"
          >
            <a-col :span="24">
              <a-card>
                <p>
                  <strong>{{ labels.view.status.label }}: </strong>
                  <a-tag :color="statusColor(identityVerification.status)">{{
                    statusText(identityVerification.status)
                  }}</a-tag>
                </p>
                <p>
                  <strong>{{ labels.view.createdAt.label }}:</strong>
                  {{ identityVerification.created_at | formatDate(dateFormat) }}
                </p>
                <!-- comments -->
                <p v-if="identityVerification.review_comments">
                  <strong>{{ labels.view.notes.label }}:</strong>
                  {{ identityVerification.review_comments }}
                </p>
                <!-- reviewed_at -->
                <p v-if="identityVerification.reviewed_at">
                  <strong>{{ labels.view.reviewedAt.label }}:</strong>
                  {{
                    identityVerification.reviewed_at | formatDate(dateFormat)
                  }}
                </p>
                <!-- reviewed_by -->
                <p v-if="identityVerification.reviewed_by">
                  <strong>{{ labels.view.reviewedBy.label }}:</strong>
                  {{ identityVerification.reviewed_by.name }} ({{
                    identityVerification.reviewed_by.email
                  }})
                </p>
              </a-card>
            </a-col>
          </a-row>
          <!-- STATUSES HISTORY COLAPSABLE -->
          <a-collapse
            v-if="
              identityVerification &&
              identityVerification.status_history.length > 0
            "
          >
            <a-collapse-panel
              :header="labels.view.statusHistory.title"
              :key="1"
            >
              <a-timeline>
                <a-timeline-item
                  v-for="(
                    statusItem, index
                  ) in identityVerification.status_history"
                  :key="index"
                  :color="statusColor(statusItem.status)"
                >
                  <strong>{{ statusText(statusItem.status) }}</strong> -
                  {{ statusItem.changed_at | formatDate(dateFormat) }}
                  <!-- changed_by -->
                  <br />
                  <span v-if="statusItem.changed_by">
                    by {{ statusItem.changed_by.name }} ({{
                      statusItem.changed_by.email
                    }})
                  </span>
                </a-timeline-item>
              </a-timeline>
            </a-collapse-panel>
          </a-collapse>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import labels from "@/utils/labels";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import {
  DATE_FORMAT,
  adminPageTitle,
  hasRoles,
  roles,
} from "../../../utils/utils";
import moment from "moment";

const initialFormState = {
  id: null,
  status: null,
  notes: null,
  send_user_notification: true,
};

export default {
  name: "IdentityVerificationView",
  components: {},
  metaInfo: {
    title: adminPageTitle(labels.identityVerifications.title),
    titleTemplate: () => {
      return `${adminPageTitle()} - ${labels.identityVerifications.view.title}`;
    },
  },
  filters: {
    formatDate(value, format) {
      if (value) {
        return moment(value).format(format);
      }
      return "-";
    },
  },
  data() {
    return {
      labels: {
        ...labels.identityVerifications,
      },
      loading: false,
      submitting: false,
      form: _.cloneDeep(initialFormState),
      errors: [],
    };
  },
  beforeRouteEnter(_to, _from, next) {
    next(async (vm) => {
      await vm.fetchData();
    });
  },
  async created() {
    this.resetForm();
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    ...mapGetters({
      loggedUser: "adminAuth/getLoggedUser",
      identityVerification: "identityVerification/getIdentityVerification",
    }),
    isMobile() {
      return this.screenWidth < 768;
    },
    dateFormat() {
      return DATE_FORMAT.MOMENT_DATE_TIME;
    },
    serverDateFormat() {
      return DATE_FORMAT.SERVER_DATE;
    },
    isAuthorizedToReview() {
      const { accessRoles } = this.loggedUser;
      return hasRoles(accessRoles, [
        roles.admin.ROLE_USERS_IDENTITY_VERIFICATIONS_REVIEW,
      ]);
    },
  },
  methods: {
    ...mapActions({
      fetchIdentityVerificationById:
        "identityVerification/fetchIdentityVerificationById",
      reviewIdentityVerification:
        "identityVerification/reviewIdentityVerification",
    }),
    resetForm() {
      this.form = _.cloneDeep(initialFormState);
    },
    handleCancel() {
      this.$router.push({ name: "admin.identity-verifications" });
    },
    handlePreviewFile(URI) {
      // CREATE IMG AND OPEN IN NEW TAB
      const img = document.createElement("img");
      img.src = URI;
      img.style.maxWidth = "520px";
      const w = window.open("about:blank", "_blank", "width=520,height=auto");
      w?.document.write(img.outerHTML);
    },
    async fetchData() {
      try {
        this.loading = true;
        await this.fetchIdentityVerificationById(this.$route.params.id);
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
        this.$log.error(error);
        this.$router.push({ name: "admin.identity-verifications" });
      } finally {
        this.loading = false;
      }
    },
    statusColor(status = "approved") {
      switch (status) {
        case "approved":
          return "#28a745";
        case "pending":
          return "#ffc107";
        case "rejected":
          return "#f50";
        default:
          return "default";
      }
    },
    statusText(status = this.labels.statuses.approved) {
      return this.labels.statuses[status] ?? " - ";
    },
    async onlyAlfa(value = "", field) {
      this.form[field] = await value.replace(/[^a-zA-Z\s]/g, "");
    },
    confirmReject() {
      this.$confirm({
        title: this.labels.confirmReject.title,
        content: (h) =>
          h(
            "div",
            {
              class: "confirm-reject-content",
            },
            [
              h("p", this.labels.confirmReject.subtitle),
              h(
                "a-form-item",
                {
                  props: {
                    label: this.labels.view.notes.label,
                    labelCol: { span: 24 },
                  },
                },
                [
                  h("a-textarea", {
                    props: {
                      placeholder: this.labels.view.notes.placeholder,
                      value: this.form.notes,
                      allowClear: true,
                      autoSize: { minRows: 3, maxRows: 5 },
                    },
                    on: {
                      input: (event) => {
                        this.form.notes = event.target.value;
                      },
                    },
                  }),
                ]
              ),
              // checkbox for notify user 'send_user_notification'
              h(
                "a-form-item",
                {
                  props: {
                    labelCol: { span: 24 },
                  },
                },
                [
                  h(
                    "a-checkbox",
                    {
                      props: {
                        checked: this.form.send_user_notification,
                      },
                      on: {
                        change: (event) => {
                          this.form.send_user_notification =
                            event.target.checked;
                        },
                      },
                    },
                    this.labels.view.sendUserNotification
                  ),
                ]
              ),
            ]
          ),
        okText: this.labels.confirmReject.confirmButtonText,
        okType: "danger",
        icon: "close-circle",
        cancelText: this.labels.confirmReject.cancelButtonText,
        onOk: async () => {
          try {
            await this.reviewIdentityVerification({
              id: this.form.id,
              ...(this.form.notes && {
                notes: this.form.notes,
              }),
              send_user_notification: this.form.send_user_notification,
              status: "rejected",
            });
            this.$message.success(this.labels.review.rejectedSuccess);
          } catch (error) {
            this.$message.error(
              error?.response?.data?.message ||
                error?.response?.message ||
                error.message ||
                this.labels.review.rejectedError
            );
          }
        },
      });
    },
    confirmApprove() {
      try {
        this.$confirm({
          title: this.labels.confirmApprove.title,
          content: this.labels.confirmApprove.subtitle,
          okText: this.labels.confirmApprove.confirmButtonText,
          okType: "danger",
          icon: "question-circle",
          cancelText: this.labels.confirmApprove.cancelButtonText,
          visible: true,
          okButtonProps: {
            disabled: this.submitting,
            loading: this.submitting,
          },
          onOk: async () => {
            try {
              await this.reviewIdentityVerification({
                id: this.form.id,
                status: "approved",
              });
              this.$message.success(this.labels.review.approvedSuccess);
            } catch (error) {
              this.$message.error(
                error?.response?.data?.message ||
                  error?.response?.message ||
                  error.message ||
                  this.labels.review.approvedError
              );
            }
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
  watch: {
    identityVerification: {
      handler(val) {
        Object.assign(this.form, { id: val.id });
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.form = _.cloneDeep(initialFormState);
    this.resetForm();
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/variable";
.identity-verification-view {
  .form-title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  .form-icon {
    margin-right: 8px;
  }
  .form-button-group {
    margin-top: 16px;
  }
}
::v-deep {
  .confirm-reject-content {
    .ant-form-item-label label {
      font-weight: 600 !important;
    }
  }
  .anticon-delete {
    color: color(danger) !important;
    font-size: 20px;
    margin-right: 10px;
  }
  .ant-upload {
    width: 100%;
  }
  .confirm-preview {
    display: flex;
    width: 100%;
    flex-direction: column;
    .confirm-preview-content {
      display: flex;
      flex-direction: column;
      gap: 10px;
      .confirm-preview-content-item {
        display: flex;
        flex-direction: row;
        gap: 10px;
        .confirm-preview-content-item-label {
          font-weight: 600;
          &:after {
            content: ":";
          }
        }
        .confirm-preview-content-item-value {
          font-weight: 400;
          &.money {
            font-weight: 500;
          }
        }
      }
    }
  }
  .flex-reserve {
    flex-direction: column-reverse !important;
  }
}
</style>
